import { LitElement, html, css, nothing } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { Router } from "@lit-labs/router";
import { api, ApiError, AUTH_EXPIRED_EVENT } from "./api";
import { getRouteLabel } from "./router";
import { formatProductName } from "./product-utils";
import type { UserInfo, Snapshot, EnvironmentsResponse, ProductsResponse } from "./types";
import type { VersionsPage } from "./pages/versions-page";

import "@bee/pattern-library";
import "./components/login-view";
import "./components/sidebar";
import "./pages/dashboard-page";
import "./pages/promotions-page";
import "./pages/versions-page";
import "./pages/snapshots-page";
import "./pages/presets-page";
import "./pages/audit-page";
import "./pages/preview-page";
import "./pages/deployments-page";
import "./pages/flags-page";
import "./components/register-mfe-dialog";

declare const __DASHBOARD_VERSION__: string;

const PRODUCT_FILTER_STORAGE_KEY = "hawaii-dashboard-active-product-filter";

@customElement("dashboard-app")
export class DashboardApp extends LitElement {
  private _router = new Router(this, [
    { path: "/", render: () => html`<dashboard-page .activeProductFilter=${this._activeProductFilter} .products=${this._products}></dashboard-page>` },
    { path: "/promotions", render: () => html`<promotions-page .activeProductFilter=${this._activeProductFilter} .products=${this._products}></promotions-page>` },
    { path: "/versions", render: () => html`<versions-page .activeProductFilter=${this._activeProductFilter} .products=${this._products} @apply-snapshot=${(e: CustomEvent<Snapshot>) => this._handleApplySnapshot(e)} @environments-change=${(e: CustomEvent<EnvironmentsResponse>) => this._handleEnvironmentsChange(e)}></versions-page>` },
    { path: "/snapshots", render: () => html`<snapshots-page .environments=${this._environments} .activeProductFilter=${this._activeProductFilter} .products=${this._products}></snapshots-page>` },
    { path: "/presets", render: () => html`<presets-page .environments=${this._environments} .activeProductFilter=${this._activeProductFilter} .products=${this._products}></presets-page>` },
    { path: "/audit", render: () => html`<audit-page .activeProductFilter=${this._activeProductFilter}></audit-page>` },
    { path: "/preview", render: () => html`<preview-page .environments=${this._environments} .activeProductFilter=${this._activeProductFilter} .products=${this._products}></preview-page>` },
    { path: "/bundles", render: () => html`<deployments-page .activeProductFilter=${this._activeProductFilter} .products=${this._products} .environments=${this._environments}></deployments-page>` },
    { path: "/flagship", render: () => html`<flags-page .activeProductFilter=${this._activeProductFilter} .products=${this._products}></flags-page>` },
  ], {
    fallback: { render: () => html`<dashboard-page .activeProductFilter=${this._activeProductFilter} .products=${this._products}></dashboard-page>` },
  });

  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      font-family: Geist, sans-serif;
    }

    .main {
      margin-inline-start: 72px;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
    }

    .page-header {
      margin-top: 4px;
      margin-left: 16px;
      border-bottom: 1px solid #d0d5dd;
      background: #fcfcfd;
      padding-bottom: 8px;
    }

    .page-header-top {
      display: flex;
      align-items: center;
      gap: var(--spacing-12, 12px);
    }

    .page-title-wrap {
      position: relative;
      display: inline-flex;
      width: fit-content;
      padding-right: 128px;
    }

    .page-title {
      height: 38px;
      margin: 0;
      color: var(--colors-text-brand-900, #002266);
      font-family: Geist, sans-serif;
      font-size: 30px;
      font-style: normal;
      font-weight: 600;
      line-height: 38px;
    }

    .page-title-product {
      vertical-align: top;
      margin-left: var(--spacing-8, 8px);
      font-size: 16px;
      font-weight: 400;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .status-tags {
      position: absolute;
      top: 0;
      right: 0;
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
    }

    .status-tag {
      text-transform: uppercase;
    }

    @media (max-width: 720px) {
      .page-title-wrap {
        padding-right: 0;
        padding-top: 28px;
      }

      .status-tags {
        top: 0;
        right: auto;
        left: 0;
      }
    }

    .page-subtitle {
      margin: 0;
      color: var(--colors-text-brand-900, #002266);
      font-family: Geist, sans-serif;
      font-size: 20px;
      font-weight: 600;
      line-height: 30px;
    }

    .content {
      max-width: 1400px;
      margin: 0 auto;
      padding: var(--spacing-8, 8px) var(--spacing-16, 16px) var(--spacing-16, 16px);
      width: 100%;
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .product-filter {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      flex-wrap: wrap;
      padding: var(--spacing-8, 8px) 0 var(--spacing-4, 4px);
    }

    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 60vh;
    }
  `;

  @query("versions-page") private _envOverview!: VersionsPage;

  @state() private _user: UserInfo | null = null;
  @state() private _loading = true;
  @state() private _error = "";
  @state() private _environments: EnvironmentsResponse | null = null;
  @state() private _currentPath = window.location.pathname;
  @state() private _products: ProductsResponse | null = null;
  @state() private _activeProductFilter: string | null = null;
  @state() private _showRegisterDialog = false;

  async connectedCallback() {
    super.connectedCallback();
    window.addEventListener("popstate", this._onPopState);
    window.addEventListener(AUTH_EXPIRED_EVENT, this._handleAuthExpired);
    await this._checkAuth();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("popstate", this._onPopState);
    window.removeEventListener(AUTH_EXPIRED_EVENT, this._handleAuthExpired);
  }

  private _onPopState = () => {
    this._currentPath = window.location.pathname;
  };

  private _handleAuthExpired = () => {
    this._user = null;
    this._loading = false;
    this._error = "";
    this._environments = null;
    this._products = null;
  };

  private async _checkAuth() {
    try {
      this._user = await api.getUser();
      await this._loadProducts();
      void this._loadEnvironments();
      this._restorePostAuthUrl();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        this._user = null;
      } else {
        this._error = "Failed to check authentication status";
      }
    } finally {
      this._loading = false;
    }
  }

  private _restorePostAuthUrl() {
    const returnUrl = sessionStorage.getItem("auth-return-url");
    if (!returnUrl) return;
    sessionStorage.removeItem("auth-return-url");
    sessionStorage.removeItem("auth-redirect-ts");
    if (returnUrl !== "/" && returnUrl !== window.location.pathname) {
      this._handleNavigate(new CustomEvent("navigate", { detail: returnUrl }));
    }
  }

  private async _loadEnvironments() {
    try {
      this._environments = await api.getEnvironments();
    } catch {
      this._environments = null;
    }
  }

  private async _loadProducts() {
    try {
      const products = await api.getProducts();
      const productNames = Object.keys(products.products).sort();
      const persistedProduct = this._loadPersistedProductFilter();

      this._products = products;

      if (productNames.length === 0) {
        this._setActiveProductFilter(null);
        return;
      }

      if (persistedProduct && persistedProduct in products.products) {
        this._setActiveProductFilter(persistedProduct);
        return;
      }

      if (!this._activeProductFilter || !(this._activeProductFilter in products.products)) {
        this._setActiveProductFilter(productNames[0]);
      }
    } catch {
      // Product filter is non-critical — silently degrade
    }
  }

  private _loadPersistedProductFilter(): string | null {
    try {
      return localStorage.getItem(PRODUCT_FILTER_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private _persistProductFilter(product: string | null) {
    try {
      if (product) {
        localStorage.setItem(PRODUCT_FILTER_STORAGE_KEY, product);
      } else {
        localStorage.removeItem(PRODUCT_FILTER_STORAGE_KEY);
      }
    } catch {
      // Product filter persistence is non-critical — silently degrade
    }
  }

  private _setActiveProductFilter(product: string | null) {
    this._activeProductFilter = product;
    this._persistProductFilter(product);
  }

  private _handleNavigate(e: CustomEvent<string>) {
    const destination = new URL(e.detail, window.location.origin);
    const routePath = `${destination.pathname}${destination.hash}`;

    window.history.pushState({}, "", `${destination.pathname}${destination.search}${destination.hash}`);
    this._router.goto(routePath);
    this._currentPath = destination.pathname;
  }

  private _handleApplySnapshot(e: CustomEvent<Snapshot>) {
    this._envOverview?.applySnapshot(e.detail);
  }

  private _handleEnvironmentsChange(e: CustomEvent<EnvironmentsResponse>) {
    this._environments = e.detail;
  }

  private async _handleLogout() {
    try {
      await api.logout();
      this._user = null;
    } catch {
      this._error = "Failed to logout";
    }
  }

  render() {
    if (this._loading) {
      return html`<div class="loading"><bee-spinner size="large" type="blue"></bee-spinner></div>`;
    }

    if (!this._user) {
      return html`<login-view></login-view>`;
    }

    const subtitle = getRouteLabel(this._currentPath);

    return html`
      ${this._error ? html`<bee-alert variant="error" open closable>
        <span slot="message">${this._error}</span>
      </bee-alert>` : ""}
      <dashboard-sidebar
        .user=${this._user}
        .currentPath=${this._currentPath}
        @logout=${() => this._handleLogout()}
        @navigate=${(e: CustomEvent<string>) => this._handleNavigate(e)}
      ></dashboard-sidebar>
      <div class="main">
        <div class="page-header">
          <div class="page-header-top">
            <div class="page-title-wrap">
              <h1 class="page-title">HAWAII Dashboard${this._activeProductFilter ? html`<span class="page-title-product">[${formatProductName(this._activeProductFilter)}]</span>` : ""}</h1>
              <div class="status-tags">
                <bee-tag class="status-tag" color="warning" small>beta</bee-tag>
                <bee-tag class="status-tag" color="gray" small>v${__DASHBOARD_VERSION__}</bee-tag>
              </div>
            </div>
          </div>
          ${this._renderProductFilter()}
        </div>
        <div
          class="content"
          @navigate=${(e: CustomEvent<string>) => this._handleNavigate(e)}
          @apply-snapshot=${(e: CustomEvent<Snapshot>) => this._handleApplySnapshot(e)}
          @environments-change=${(e: CustomEvent<EnvironmentsResponse>) => this._handleEnvironmentsChange(e)}
        >
          ${subtitle ? html`<h2 class="page-subtitle">${subtitle}</h2>` : ""}
          ${this._router.outlet()}
        </div>
      </div>
    `;
  }

  private _renderProductFilter() {
    if (!this._products) return nothing;
    const productNames = Object.keys(this._products.products).sort();
    if (productNames.length === 0) return nothing;

    return html`
      <div class="product-filter">
        <b>Product:</b>
        <bee-select
          class="product-select"
          size="small"
          .value=${this._activeProductFilter ?? ""}
          @bee-change=${(e: CustomEvent) => this._setActiveProductFilter((e.target as HTMLSelectElement).value)}
        >
          ${productNames.map((name) => html`
            <bee-option value=${name}>${formatProductName(name)}</bee-option>
          `)}
        </bee-select>
        <bee-button
          label="+ Add Product"
          bee-type="tertiary"
          size="small"
          @click=${() => { this._showRegisterDialog = true; }}
        ></bee-button>
      </div>
      <register-mfe-dialog
        ?open=${this._showRegisterDialog}
        @close=${() => { this._showRegisterDialog = false; }}
      ></register-mfe-dialog>
    `;
  }
}
